import { Scene } from '@babylonjs/core/scene';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import type { CascadedShadowGenerator } from '@babylonjs/core/Lights/Shadows/cascadedShadowGenerator';
import { RANGE_LENGTH, RANGE_WIDTH, YARD_MARKERS, M_TO_YARDS } from '../constants';
import { getRuntimeProfile } from '../core/RuntimeProfile';

export function buildDrivingRange(scene: Scene, shadowGen: CascadedShadowGenerator): void {
  const profile = getRuntimeProfile();
  buildDigitalSurround(scene);

  // ── Ground Plane — dark matte surface ──
  const ground = MeshBuilder.CreateGround('ground', {
    width: RANGE_WIDTH,
    height: RANGE_LENGTH,
    subdivisions: profile.groundSubdivisions,
  }, scene);
  ground.position = new Vector3(0, 0, RANGE_LENGTH / 2);
  ground.receiveShadows = true;

  const groundMat = new StandardMaterial('groundMat', scene);
  groundMat.emissiveTexture = createDigitalGrid(scene);
  groundMat.diffuseColor = Color3.Black();
  // StandardMaterial adds emissiveColor to the texture; keep it black so the
  // grid's dark pixels remain dark instead of lighting the entire floor.
  groundMat.emissiveColor = Color3.Black();
  groundMat.ambientColor = Color3.Black();
  groundMat.specularColor = Color3.Black();
  groundMat.disableLighting = true;
  ground.material = groundMat;

  // ── Tee Area — slightly lighter pad ──
  const teeBox = MeshBuilder.CreateGround('teeBox', { width: 3, height: 4 }, scene);
  teeBox.position = new Vector3(0, 0.003, 1);
  teeBox.receiveShadows = true;
  const teeMat = new StandardMaterial('teeMat', scene);
  teeMat.diffuseColor = new Color3(0.014, 0.016, 0.02);
  teeMat.specularColor = new Color3(0.01, 0.01, 0.01);
  teeBox.material = teeMat;

  // ── Tee marker — small white cylinder ──
  const teePeg = MeshBuilder.CreateCylinder('teePeg', {
    height: 0.04,
    diameterTop: 0.015,
    diameterBottom: 0.01,
  }, scene);
  teePeg.position = new Vector3(0, 0.02, 0);
  const teePegMat = new StandardMaterial('teePegMat', scene);
  teePegMat.diffuseColor = new Color3(0.8, 0.8, 0.8);
  teePegMat.emissiveColor = new Color3(0.2, 0.2, 0.2);
  teePeg.material = teePegMat;
  shadowGen.addShadowCaster(teePeg);

  // ── Distance Markers ──
  for (const yardDist of YARD_MARKERS) {
    const meterDist = yardDist / M_TO_YARDS;
    createYardMarker(scene, yardDist, meterDist, profile.markerRingSegments);
  }

  // ── Calibration rails and origin reticle ──
  createDigitalGuides(scene);
}

/** Render-only surface. Collision, ball contact and turf physics are unchanged. */
function buildDigitalSurround(scene: Scene): void {
  const surround = MeshBuilder.CreateGround('range-surround', { width: 1600, height: 1600 }, scene);
  surround.position.set(0, -0.03, 200);
  surround.isPickable = false;
  const material = new StandardMaterial('digital-void-floor', scene);
  material.diffuseColor = Color3.Black();
  material.emissiveColor = new Color3(0.002, 0.003, 0.004);
  material.disableLighting = true;
  surround.material = material;
}

/** One mipmapped grid texture instead of many thin, shimmering floor meshes.
 * Each tile spans 100 metres: fine divisions at 5 m, major divisions at 20 m. */
function createDigitalGrid(scene: Scene): DynamicTexture {
  const size = 1024;
  const texture = new DynamicTexture('digital-coordinate-grid', size, scene, true);
  // DynamicTexture defaults to clamping; explicit repeat prevents large bands
  // of stretched edge pixels outside the first 100-metre tile.
  texture.wrapU = Texture.WRAP_ADDRESSMODE;
  texture.wrapV = Texture.WRAP_ADDRESSMODE;
  texture.uScale = RANGE_WIDTH / 100;
  texture.vScale = RANGE_LENGTH / 100;
  texture.uOffset = 0.5;
  texture.anisotropicFilteringLevel = 4;
  texture.level = 0.5;
  const context = texture.getContext();
  context.fillStyle = '#000000';
  context.fillRect(0, 0, size, size);
  for (let index = 0; index < 20; index++) {
    const major = index % 4 === 0;
    const offset = index * size / 20;
    // Cool cyan minors and violet majors suggest an instrument display while
    // staying dim enough that trajectory colors retain hierarchy.
    context.fillStyle = major ? '#3b3654' : '#152a34';
    const width = major ? 4 : 2;
    context.fillRect(offset, 0, width, size);
    context.fillRect(0, offset, size, width);
  }
  texture.update();
  return texture;
}

/**
 * Generate points for a flat circle outline at y height.
 */
function circlePoints(cx: number, cz: number, radius: number, y: number, segments: number): Vector3[] {
  const pts: Vector3[] = [];
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    pts.push(new Vector3(
      cx + Math.cos(angle) * radius,
      y,
      cz + Math.sin(angle) * radius,
    ));
  }
  return pts;
}

/**
 * Create a yardage text label on the ground using DynamicTexture.
 */
function createYardageLabel(scene: Scene, text: string, x: number, z: number): void {
  const planeSize = 6;
  const plane = MeshBuilder.CreatePlane(`label-${text}`, { width: planeSize, height: planeSize * 0.4 }, scene);
  plane.position = new Vector3(x, 0.035, z);
  plane.rotation.x = Math.PI / 2; // lay flat on ground

  const resolution = 256;
  const tex = new DynamicTexture(`labelTex-${text}`, { width: resolution * 2.5, height: resolution }, scene, false);
  tex.hasAlpha = true;

  const ctx = tex.getContext();
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.font = `500 ${Math.floor(resolution * 0.7)}px monospace`;
  ctx.fillStyle = 'rgba(211, 240, 250, 0.9)';
  (ctx as any).textAlign = 'center';
  (ctx as any).textBaseline = 'middle';
  ctx.fillText(text, ctx.canvas.width / 2, ctx.canvas.height / 2);
  tex.update();

  const mat = new StandardMaterial(`labelMat-${text}`, scene);
  mat.diffuseTexture = tex;
  mat.emissiveColor = new Color3(0.56, 0.70, 0.78);
  mat.opacityTexture = tex;
  mat.disableLighting = true;
  mat.backFaceCulling = false;
  plane.material = mat;
}

function createYardMarker(
  scene: Scene,
  yards: number,
  meters: number,
  ringSegments: number,
): void {
  // Thin horizontal marker line
  const line = MeshBuilder.CreateGround(`marker-${yards}`, {
    width: 50,
    height: 0.15,
  }, scene);
  line.position = new Vector3(0, 0.025, meters);
  line.receiveShadows = true;

  const lineMat = new StandardMaterial(`markerMat-${yards}`, scene);
  lineMat.diffuseColor = Color3.Black();
  lineMat.emissiveColor = new Color3(0.30, 0.50, 0.60);
  lineMat.disableLighting = true;
  lineMat.alpha = 0.25;
  line.material = lineMat;

  // Flat circle outlines using CreateLines — guaranteed flat on the ground
  const y = 0.03;
  const outerColor = new Color4(0.49, 0.83, 0.92, 0.48);
  const innerColor = new Color4(0.61, 0.53, 0.92, 0.3);

  // Outer ring
  const outerPts = circlePoints(0, meters, 4, y, ringSegments);
  const outerColors = outerPts.map(() => outerColor);
  MeshBuilder.CreateLines(`ring-${yards}`, {
    points: outerPts,
    colors: outerColors,
  }, scene);

  // Inner ring
  const innerPts = circlePoints(0, meters, 1.5, y, Math.max(24, ringSegments - 16));
  const innerColors = innerPts.map(() => innerColor);
  MeshBuilder.CreateLines(`innerRing-${yards}`, {
    points: innerPts,
    colors: innerColors,
  }, scene);

  // Yardage text label — offset to the right of center so it doesn't overlap the line
  createYardageLabel(scene, `${yards}`, 8, meters);
}

function createDigitalGuides(scene: Scene): void {
  const lines: Vector3[][] = [];
  const colors: Color4[][] = [];
  const railColor = new Color4(0.39, 0.68, 0.77, 0.24);
  const add = (a: Vector3, b: Vector3, color = railColor) => {
    lines.push([a, b]); colors.push([color, color]);
  };
  for (const x of [-40, 40]) {
    add(new Vector3(x, 0.03, 0), new Vector3(x, 0.03, RANGE_LENGTH));
    for (let z = 0; z <= RANGE_LENGTH; z += 20) {
      add(new Vector3(x - 1, 0.03, z), new Vector3(x + 1, 0.03, z));
    }
  }
  // Broken target axis keeps the shot tracer visually distinct from the guides.
  for (let z = 4; z < RANGE_LENGTH; z += 10) {
    add(new Vector3(0, 0.03, z), new Vector3(0, 0.03, z + 2), new Color4(0.44, 0.88, 0.94, 0.36));
  }
  for (const x of [-1.8, 1.8]) {
    for (const z of [-1.8, 1.8]) {
      add(new Vector3(x, 0.035, z), new Vector3(x - Math.sign(x) * 0.7, 0.035, z), new Color4(0.94, 0.48, 0.79, 0.72));
      add(new Vector3(x, 0.035, z), new Vector3(x, 0.035, z - Math.sign(z) * 0.7), new Color4(0.94, 0.48, 0.79, 0.72));
    }
  }
  const guides = MeshBuilder.CreateLineSystem('digital-calibration-guides', { lines, colors }, scene);
  guides.isPickable = false;
}
